import React from 'react';

import PropTypes from 'prop-types';

import { escapeRegex } from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import {
  stylePropType,
  StylableComponent,
  StyleProp,
} from '@adsk/alloy-react-theme';

type HighlightTextProps = StylableComponent<HTMLSpanElement> & {
  matched?: string | string[];
  text?: string;
  className?: string;
  highlightStyle?: StyleProp;
};

type PartialText = {
  word: string;
  highlight: boolean;
};

const HighlightText = React.forwardRef<HTMLSpanElement, HighlightTextProps>(
  ({ className, style, text, matched, highlightStyle, ...rest }, ref) => {
    const theme = useTheme();
    const highlightTextTokens = theme.tokens?.component?.highlighttext;

    if (!text) return null;
    if (!matched)
      return (
        <span
          ref={ref}
          dir="auto"
          css={[style]}
          className={className}
          {...rest}
        >
          {text}
        </span>
      );

    const keywords = Array.isArray(matched) ? matched : [matched];
    const partials = [...keywords]
      .sort((va, vb) => (va.length > vb.length ? -1 : 1))
      .reduce(
        (pre: PartialText[], keyword: string) => {
          const splitter = new RegExp(`(${escapeRegex(keyword)})`, 'ig');
          return pre.reduce((blocks: PartialText[], block: PartialText) => {
            if (block.highlight) return [...blocks, block];
            return [
              ...blocks,
              ...block.word.split(splitter).map((txt: string) => ({
                word: txt,
                highlight: new RegExp(`^${escapeRegex(keyword)}$`, 'i').test(
                  txt,
                ),
              })),
            ];
          }, []);
        },
        [{ word: text, highlight: false }],
      );

    return (
      <span ref={ref} dir="auto" css={[style]} className={className} {...rest}>
        {partials.map((partial: PartialText, index: number) => (
          <span
            key={index}
            css={
              partial.highlight
                ? [
                    {
                      backgroundColor:
                        highlightTextTokens?.['background-color'],
                    },
                    highlightStyle,
                  ]
                : undefined
            }
          >
            {partial.word}
          </span>
        ))}
      </span>
    );
  },
);

HighlightText.displayName = 'HighlightText';

HighlightText.propTypes = {
  /** The highlighted matched text */
  matched: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string.isRequired),
  ]),
  /** The original text */
  text: PropTypes.string,
  /** Styles applied to root element */
  style: stylePropType,
  /** Styled applied to matched highlight element */
  highlightStyle: stylePropType,
  /** ClassName applied to root element */
  className: PropTypes.string,
};

export default HighlightText;
